import React, { createContext, useContext, useState, useCallback, useEffect, useRef } from 'react';
import api from '../lib/api';
import { toast } from 'sonner';

const DashboardContext = createContext(null);

const CACHE_DURATION = 30 * 1000; // 30 seconds
const CACHE_KEY = 'dashboardCache_v3';

export const DashboardProvider = ({ children }) => {
  const [dashboardData, setDashboardData] = useState(null);
  const [loading, setLoading] = useState(false);

  // Refs mirror state so fetch fn can stay stable (no deps) and not re-create each render,
  // which otherwise cascades into infinite effect re-runs in consumers.
  const dataRef = useRef(null);
  const lastFetchTimeRef = useRef(null);
  const inFlightRef = useRef(null);

  // Restore from localStorage on mount
  useEffect(() => {
    try {
      const cached = localStorage.getItem(CACHE_KEY);
      if (!cached) return;
      const parsed = JSON.parse(cached);
      if (parsed?.data && parsed?.ts) {
        dataRef.current = parsed.data;
        lastFetchTimeRef.current = parsed.ts;
        setDashboardData(parsed.data);
      }
    } catch (error) {
      console.error('Failed to read dashboard cache:', error);
    }
  }, []);

  const fetchDashboardData = useCallback(async (forceRefresh = false) => {
    // Return cached data if available and not expired
    if (!forceRefresh && dataRef.current && lastFetchTimeRef.current) {
      const timeSinceLastFetch = Date.now() - lastFetchTimeRef.current;
      if (timeSinceLastFetch < CACHE_DURATION) {
        return dataRef.current;
      }
    }

    // Coalesce concurrent calls onto the same network promise
    if (inFlightRef.current) return inFlightRef.current;

    const isColdStart = !dataRef.current;
    if (isColdStart) setLoading(true);

    const run = (async () => {
      try {
        const [dashStatsRes, reportsStatsRes, grievancesStatsRes, sentimentRes, categoryAnalyticsRes] = await Promise.all([
          api.get('/alerts/dashboard-stats'),
          api.get('/reports/stats'),
          api.get('/grievances/dashboard-stats'),
          api.get('/grievances/sentiment-analytics').catch(() => ({ data: null })),
          api.get('/grievances/category-analytics').catch(() => ({ data: null }))
        ]);

        const { byPlatform, pendingByPlatform, viralByPlatform } = dashStatsRes.data;

        const statuses = ['active', 'acknowledged', 'escalated', 'false_positive'];
        const alertData = {};
        statuses.forEach(status => {
          alertData[status] = {};
          Object.keys(byPlatform).forEach(plat => {
            alertData[status][plat] = byPlatform[plat]?.[status] || 0;
          });
        });
        alertData.viral = viralByPlatform;

        const alertPendingReportData = pendingByPlatform;
        const reportData = reportsStatsRes.data?.byPlatform || {};
        const grievanceData = grievancesStatsRes.data?.byPlatform || {};

        const newData = {
          alertData,
          alertPendingReportData,
          reportData,
          grievanceData,
          sentimentAnalytics: sentimentRes.data || null,
          categoryAnalytics: categoryAnalyticsRes.data || null
        };

        dataRef.current = newData;
        lastFetchTimeRef.current = Date.now();
        setDashboardData(newData);
        try {
          localStorage.setItem(CACHE_KEY, JSON.stringify({ data: newData, ts: lastFetchTimeRef.current }));
        } catch (error) {
          console.error('Failed to write dashboard cache:', error);
        }
        return newData;
      } catch (error) {
        toast.error('Failed to load dashboard data');
        console.error(error);
        return dataRef.current;
      } finally {
        if (isColdStart) setLoading(false);
        inFlightRef.current = null;
      }
    })();

    inFlightRef.current = run;
    return run;
  }, []);

  const refreshDashboard = useCallback(() => fetchDashboardData(true), [fetchDashboardData]);

  return (
    <DashboardContext.Provider value={{
      dashboardData,
      loading,
      fetchDashboardData,
      refreshDashboard,
      hasCachedData: !!dashboardData
    }}>
      {children}
    </DashboardContext.Provider>
  );
};

export const useDashboard = () => {
  const context = useContext(DashboardContext);
  if (!context) {
    throw new Error('useDashboard must be used within a DashboardProvider');
  }
  return context;
};

export default DashboardContext;
